import { useTheme } from "next-themes";
import { Suspense, lazy, useEffect, useState } from "react";

/**
 * The landing hero's animated backdrop.
 *
 * Two things happen here, both for the same reason — this sits directly behind
 * the LCP element on the highest-traffic page:
 *
 * 1. `ogl` is code-split. It is a WebGL renderer and has no business in the
 *    entry chunk that every route, including the crawler-facing ones, downloads.
 * 2. Exactly one canvas mounts. The light and dark variants used to render
 *    together with one hidden by CSS, which meant two live WebGL contexts
 *    animating on every visit and only one of them visible.
 *
 * It is decorative, so it renders nothing on the server and nothing before the
 * theme resolves.
 */
const GradientWaves = lazy(() =>
  import("./gradient-waves").then((m) => ({ default: m.GradientWaves })),
);

const SHARED = {
  speed: 0.35,
  amplitude: 2.5,
  waveScale: 0.6,
  waveRatio: 0.9,
  swell: 35,
  turbulence: 20,
  tilt: 1.11,
  zoom: 1.0,
  height: 5.5,
  fogDepth: 15,
  detail: "medium",
  brightness: 1.0,
  opacity: 1.0,
  mouseInteraction: true,
  parallaxStrength: 0.5,
  className: "h-full w-full",
} as const;

export function HeroWaves() {
  const { resolvedTheme } = useTheme();
  const [mounted, setMounted] = useState(false);

  useEffect(() => setMounted(true), []);
  if (!mounted) return null;

  const dark = resolvedTheme === "dark";

  return (
    <div
      aria-hidden="true"
      className={`pointer-events-none absolute inset-0 -z-10 h-full w-full [mask-image:linear-gradient(to_bottom,black_60%,transparent_100%)] ${
        dark ? "opacity-25" : "opacity-70"
      }`}
    >
      <Suspense fallback={null}>
        {dark ? (
          <GradientWaves
            {...SHARED}
            horizonColor="#150628"
            waveColor="#4c1d95"
            crestColor="#8b5cf6"
            grain
            grainIntensity={0.04}
          />
        ) : (
          // Waves look bad over white at any opacity, so light mode gets the
          // pale palette rather than the same one at lower alpha.
          <GradientWaves
            {...SHARED}
            horizonColor="#ffffff"
            waveColor="#c4b5fd"
            crestColor="#ede9fe"
            grain={false}
          />
        )}
      </Suspense>
    </div>
  );
}
